import React from "react"
import PropTypes from "prop-types"
import { Avatar, Badge } from "@material-ui/core"
import { withStyles } from "@material-ui/core/styles"
import { User } from "lucide-react"
import useStyles from "./styles"
import { obterLogoCanal } from "../../utils/canaisMap"
import { useTheme } from "@material-ui/core"
import resolverImagemPerfil from "../../helpers/resolverImagemPerfil"
import obterIniciaisDoNome from "../../helpers/obterIniciaisDoNome"
import obterEstiloAvatarAbreviacao from "../../helpers/obterEstiloAvatarAbreviacao"

const oPropsImagemLazy = { loading: "lazy", decoding: "async" }

// Badge customizado para logos de canais (WhatsApp, interno, etc.)
const StyledBadgeWithLogo = withStyles((theme) => ({
  badge: {
    width: 16,
    height: 16,
    borderRadius: theme.radius?.radius200 || "4px",
    backgroundColor: "transparent",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      borderRadius: theme.radius?.radius200 || "4px",
      WebkitUserDrag: "none",
    },
    "&:hover": {
      width: 18,
      height: 18,
    },
  },
}))(Badge)

const AvatarComBadge = ({
  cSrcPrincipal,
  cNomePrincipal,
  cNomeAbreviacao,
  cSrcSecundario,
  cNomeSecundario,
  cNomeAbreviacaoSecundario,
  lMostrarDoisAvatares = false,
  nTamanho = 48,
  nTamanhoAvatarDuplo, // Tamanho específico quando duplo
  nTamanhoContainer, // Tamanho específico do container quando duplo
  cCorFundo,
  cCorTexto,
  cTipoCanal, // 'whatsapp', 'interno', 'telegram', 'instagram', etc.
  cLogoBase64, // Logo do canal em base64
}) => {
  const classes = useStyles({ nTamanho, lMostrarDoisAvatares, nTamanhoAvatarDuplo, nTamanhoContainer })
  const theme = useTheme()

  const { cUrlImagem: cUrlPrincipal, cTextoAvatar: cTextoPrincipal } = resolverImagemPerfil(cSrcPrincipal)
  const { cUrlImagem: cUrlSecundario, cTextoAvatar: cTextoSecundario } = resolverImagemPerfil(cSrcSecundario)

  const lEhCanalInterno = cTipoCanal === "interno" || lMostrarDoisAvatares

  const obterTextoAvatar = (cTexto, cAbreviacao, cNome) => {
    if (!lEhCanalInterno) return null
    if (cTexto) return cTexto
    if (cAbreviacao) return cAbreviacao
    if (cNome) return obterIniciaisDoNome(cNome)
    return null
  }

  const renderConteudoAvatar = (cUrl, cTexto, cAbreviacao, cNome, nIconeTamanho) => {
    if (cUrl) return null

    const cTextoExibir = obterTextoAvatar(cTexto, cAbreviacao, cNome)
    if (cTextoExibir) return cTextoExibir

    return <User size={nIconeTamanho} color={theme.palette.feedbackSuccess100} />
  }

  const obterEstiloAvatar = (cUrl, cTexto, cAbreviacao, cNome, nTamanhoAvatar, lSecundario = false) => {
    const oEstiloBase = lSecundario ? { WebkitUserDrag: "none" } : {}

    if (cUrl) {
      return {
        backgroundColor: cCorFundo,
        color: cCorTexto,
        ...oEstiloBase,
      }
    }

    const cTextoExibir = obterTextoAvatar(cTexto, cAbreviacao, cNome)

    if (cTextoExibir) {
      return {
        ...obterEstiloAvatarAbreviacao(theme, nTamanhoAvatar, { cCorFundo, cCorTexto }),
        ...oEstiloBase,
      }
    }

    return {
      backgroundColor: cCorFundo || theme.palette.backgroundSuccess,
      color: cCorTexto,
      ...oEstiloBase,
    }
  }

  const renderAvatarPrincipal = () => {
    const nTamanhoAvatar =
      lMostrarDoisAvatares && nTamanhoAvatarDuplo ? nTamanhoAvatarDuplo : lMostrarDoisAvatares ? 42 : nTamanho

    return (
      <Avatar
        src={cUrlPrincipal}
        alt={cNomePrincipal}
        imgProps={oPropsImagemLazy}
        className={classes.avatar}
        style={obterEstiloAvatar(cUrlPrincipal, cTextoPrincipal, cNomeAbreviacao, cNomePrincipal, nTamanhoAvatar)}
      >
        {renderConteudoAvatar(cUrlPrincipal, cTextoPrincipal, cNomeAbreviacao, cNomePrincipal, nTamanho * 0.5)}
      </Avatar>
    )
  }

  const renderAvatarSecundario = () => {
    const nTamanhoAvatar = nTamanhoAvatarDuplo || 42

    return (
      <Avatar
        src={cUrlSecundario}
        alt={cNomeSecundario}
        imgProps={oPropsImagemLazy}
        className={classes.avatarSecundario}
        style={obterEstiloAvatar(
          cUrlSecundario,
          cTextoSecundario,
          cNomeAbreviacaoSecundario,
          cNomeSecundario,
          nTamanhoAvatar,
          true,
        )}
      >
        {renderConteudoAvatar(
          cUrlSecundario,
          cTextoSecundario,
          cNomeAbreviacaoSecundario,
          cNomeSecundario,
          lMostrarDoisAvatares ? 16 : nTamanho * 0.5,
        )}
      </Avatar>
    )
  }

  // Dois avatares lado a lado
  if (lMostrarDoisAvatares) {
    return (
      <div className={classes.containerDoisAvatares}>
        {renderAvatarPrincipal()}
        {renderAvatarSecundario()}
      </div>
    )
  }

  // Badge com logo de canal (WhatsApp, interno, etc.)
  // Se cLogoBase64 foi passado diretamente, usa ele
  // Senão, tenta obter do map de canais pelo tipo
  const cLogoFinal = cLogoBase64 || obterLogoCanal(cTipoCanal)

  if (cLogoFinal) {
    return (
      <StyledBadgeWithLogo
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={<img src={cLogoFinal} alt={cTipoCanal || "canal"} />}
      >
        {renderAvatarPrincipal()}
      </StyledBadgeWithLogo>
    )
  }

  // Fallback: avatar sem badge
  return renderAvatarPrincipal()
}

AvatarComBadge.propTypes = {
  cSrcPrincipal: PropTypes.string,
  cNomePrincipal: PropTypes.string,
  cNomeAbreviacao: PropTypes.string,
  cSrcSecundario: PropTypes.string,
  cNomeSecundario: PropTypes.string,
  cNomeAbreviacaoSecundario: PropTypes.string,
  lMostrarDoisAvatares: PropTypes.bool,
  nTamanho: PropTypes.number,
  nTamanhoAvatarDuplo: PropTypes.number,
  nTamanhoContainer: PropTypes.number,
  cCorFundo: PropTypes.string,
  cCorTexto: PropTypes.string,
  cTipoCanal: PropTypes.oneOf(["whatsapp", "interno"]),
  cLogoBase64: PropTypes.string,
}

export default AvatarComBadge
